import React, { Suspense, useState } from 'react'
import { renderRoutes } from 'react-router-config'
import { Redirect } from 'react-router-dom'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/styles'
import { LinearProgress, useMediaQuery } from '@material-ui/core'
import NavBar from './NavBar'
import TopBar from './TopBar'
import ls from 'local-storage'
import usePermissions from 'src/utils/usePermissions'
import clsx from 'clsx'
import { useHistory } from 'react-router'
import configuration from '../../configuration'
import GoogleAnalytics from '../../components/GoogleAnalytics'
import Clarity from '../../components/Clarity'
import ScheduledMaintenanceBanner, {
  SCHEDULED_MAINTENANCE_BANNER_HEIGHT,
  SCHEDULED_MAINTENANCE_MOBILE_BANNER_HEIGHT
} from './ScheduledMaintenanceBanner'
import { useScheduledMaintenance } from './scheduledMaintenance'
import { fontFamily } from '../../views/PayInvoicesWithCreditCard/design'

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: '100vh',
    display: 'flex',
    '@media all and (-ms-high-contrast:none)': {
      height: 0 // IE11 fix
    }
  },
  content: {
    paddingTop: 64,
    flexGrow: 1,
    maxWidth: '100%',
    overflowX: 'hidden'
  },
  menuOpenContent: {
    [theme.breakpoints.up('md')]: {
      paddingLeft: '14.5vw',
      transition: 'padding-left .2s ease'
    },
    [theme.breakpoints.down('xs')]: {
      paddingTop: 56
    }
  },
  menuClosedContent: {
    [theme.breakpoints.up('md')]: {
      paddingLeft: 68,
      transition: 'padding-left .2s ease'
    },
    [theme.breakpoints.down('xs')]: {
      paddingTop: 56
    }
  },
  // Reference geometry is scoped to the card-payment route; menu behavior stays shared.
  cardPaymentContent: { '&&': { paddingTop: 58, backgroundColor: '#F7F8FB' } },
  cardPaymentOpenContent: {
    '&&': {
      [theme.breakpoints.up('md')]: { paddingLeft: 248 },
      '@media(min-width:960px) and (max-width:1120px)': { paddingLeft: 205 }
    }
  },
  cardPaymentMaintenanceContent: {
    '&&': {
      paddingTop: 58 + SCHEDULED_MAINTENANCE_BANNER_HEIGHT,
      [theme.breakpoints.down('xs')]: {
        paddingTop: 58 + SCHEDULED_MAINTENANCE_MOBILE_BANNER_HEIGHT
      }
    }
  },
  cardPaymentTopBar: {
    '&&': {
      height: 58,
      background: 'linear-gradient(100deg,#D52734 0%,#A5225E 45%,#56208F 100%)'
    },
    '& .MuiToolbar-root': { minHeight: 58, height: 58, padding: '0 22px' },
    '& .MuiToolbar-root > div:first-child': { height: 58 },
    '& img[alt=Logo]': { height: 20 },
    '& .MuiButton-endIcon img': { width: '22px !important', height: 15 },
    '& .MuiSvgIcon-root': { width: 18, height: 18 },
    '& .MuiIconButton-root': { padding: 8 }
  },
  cardPaymentNav: {
    fontFamily,
    '& .MuiTypography-root, & .MuiInputBase-root, & .MuiButton-root': {
      fontFamily
    },
    '&& .MuiOutlinedInput-root': { height: 41 },
    '& .MuiTypography-overline': {
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: '.09em'
    },
    '& .MuiListSubheader-root': { fontSize: 9 },
    '& nav': { padding: '0 8px' },
    '& .MuiButton-root': {
      minHeight: 38,
      fontSize: 11,
      fontWeight: 500,
      borderRadius: 7,
      color: '#3F5066'
    },
    '& .MuiButton-root[aria-current=page]': {
      color: '#C92939',
      fontWeight: 600,
      background: '#FFF0F2',
      '&:before': {
        height: 22,
        borderRadius: '0 3px 3px 0',
        backgroundColor: '#D72635'
      },
      '& .MuiSvgIcon-root': { color: '#D72635' }
    },
    '& .MuiSvgIcon-root': { fontSize: 15 }
  },
  maintenanceContent: {
    paddingTop: 64 + SCHEDULED_MAINTENANCE_BANNER_HEIGHT,
    [theme.breakpoints.down('xs')]: {
      paddingTop: 56 + SCHEDULED_MAINTENANCE_MOBILE_BANNER_HEIGHT
    }
  }
}))

/* Layout per l'intera dashboard. Posso accederci solo se ho fatto login */
function Dashboard ({ route }) {
  const classes = useStyles()
  const [openNavBarMobile, setOpenNavBarMobile] = useState(false)
  const [isNavBarDesktopCollapsed, setIsNavBarDesktopCollapsed] = useState(false)
  const history = useHistory()
  const requestedLocation = history.location.pathname + history.location.search
  const isCardPayment = /^\/scf\/creditCardPrograms\/[^/]+\/[^/]+\/?$/.test(history.location.pathname) &&
    !/\/(pingpong|expressDynamicDiscounting)$/.test(history.location.pathname)

  // Verifico i permessi e setto utente e token nel localstorage
  usePermissions()
  const user = ls.get('plsadv_user')
  const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'))
  const isExtraSmall = useMediaQuery(theme => theme.breakpoints.down('xs'))
  const isScheduledMaintenanceVisible = useScheduledMaintenance()
  const showScheduledMaintenance = Boolean(user) && isScheduledMaintenanceVisible
  const topOffset = isExtraSmall
    ? 56 + SCHEDULED_MAINTENANCE_MOBILE_BANNER_HEIGHT
    : 64 + SCHEDULED_MAINTENANCE_BANNER_HEIGHT
  const contentClassName = clsx(
    classes.content,
    showScheduledMaintenance && classes.maintenanceContent,
    !isSmall && !isNavBarDesktopCollapsed ? classes.menuOpenContent : classes.menuClosedContent,
    isCardPayment && classes.cardPaymentContent,
    isCardPayment && !isSmall && !isNavBarDesktopCollapsed && classes.cardPaymentOpenContent,
    isCardPayment && showScheduledMaintenance && classes.cardPaymentMaintenanceContent
  )

  return (
    <>
      {configuration.environment === 'production' ? <GoogleAnalytics /> : null}
      {configuration.environment === 'production' ? <Clarity /> : null}
      {showScheduledMaintenance ? <ScheduledMaintenanceBanner /> : null}
      <TopBar
        className={isCardPayment ? classes.cardPaymentTopBar : undefined}
        hasScheduledMaintenance={showScheduledMaintenance}
        isNavBarDesktopCollapsed={isNavBarDesktopCollapsed}
        onToggleNavBarDesktop={() => setIsNavBarDesktopCollapsed((collapsed) => !collapsed)}
        onOpenNavBarMobile={() => setOpenNavBarMobile(true)}
      />
      <NavBar
        appearance={isCardPayment ? 'cardPayment' : undefined}
        className={isCardPayment ? classes.cardPaymentNav : undefined}
        topOffset={isCardPayment ? 58 + (showScheduledMaintenance ? (isExtraSmall ? SCHEDULED_MAINTENANCE_MOBILE_BANNER_HEIGHT : SCHEDULED_MAINTENANCE_BANNER_HEIGHT) : 0) : showScheduledMaintenance ? topOffset : undefined}
        onMobileClose={() => setOpenNavBarMobile(false)}
        collapsedDesktop={isNavBarDesktopCollapsed}
        onToggleDesktop={setIsNavBarDesktopCollapsed}
        openMobile={openNavBarMobile}
        openDesktop
      />
      <div className={classes.container}>
        <div className={contentClassName}>
          <Suspense fallback={<LinearProgress />}>
            {user ? renderRoutes(route.routes) : <Redirect to={`/auth/login?requestedUrl=${requestedLocation}`} />}
          </Suspense>
        </div>
      </div>
    </>
  )
}

Dashboard.propTypes = {
  route: PropTypes.object
}

export default Dashboard
